import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Bot, RotateCcw, MoreVertical } from 'lucide-react';
import chatAPI, { ChatApiMessage } from '../../services/chatApi';
import { Reasoning, ReasoningTrigger, ReasoningContent } from '../ai/reasoning';
import { AIResponse } from '../ai/AIResponse';
import { ScrollArea } from '../ui/scroll-area';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  isLoading?: boolean;
  error?: string;
  isStreaming?: boolean;
  chunks?: string[];
  reasoning?: string;
  isReasoningStreaming?: boolean;
  reasoningDuration?: number;
  reasoningStartTime?: number;
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const suggestedQuestions = [
  "What can you help me with as a people manager at Amazon?",
  "Compare Bob's and Amy's calibrated scores.",
  "How do I help an IC L7 develop influence skills?"
];

// Memoized message component for performance optimization
const MessageItem = React.memo(({ message }: { message: Message }) => {
  // Debug logging for reasoning state
  if (message.reasoning && message.sender === 'assistant') {
    console.log('MessageItem: Rendering reasoning for message', {
      id: message.id,
      isReasoningStreaming: message.isReasoningStreaming,
      reasoningDuration: message.reasoningDuration,
      reasoning: message.reasoning.substring(0, 50) + '...'
    });
  }
  
  if (message.sender === 'user') {
    return (
      <div className="flex justify-end">
        <div 
          style={{
            backgroundColor: '#232F3E',
            color: '#FFFFFF',
            borderRadius: '20px',
            padding: '16px 20px',
            maxWidth: '70%',
            fontSize: '16px',
            lineHeight: '1.5'
          }}
        >
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div>
      {message.isLoading ? (
        <div></div>
      ) : (
        <div>
          {/* Reasoning Block */}
          {(message.reasoning !== undefined || message.isReasoningStreaming) && (
            <div style={{ marginBottom: '16px' }}>
              <Reasoning
                isStreaming={message.isReasoningStreaming || false}
                thinkingTime={message.reasoningDuration}
              >
                <ReasoningTrigger title="Thinking" />
                <ReasoningContent>
                  {message.reasoning}
                </ReasoningContent>
              </Reasoning>
            </div>
          )}
          
          {/* Main Response */}
          <div 
            style={{
              backgroundColor: 'transparent',
              color: '#0F1111',
              padding: '16px 0',
              maxWidth: '100%',
              fontSize: '16px',
              lineHeight: '1.6',
              position: 'relative'
            }}
          >
            <AIResponse 
              content={message.content}
              isStreaming={message.isStreaming}
              className="prose prose-sm max-w-none"
              fontSize="sm"
            />
            {message.isStreaming && (
              <span 
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '16px',
                  backgroundColor: '#FF6900',
                  marginLeft: '2px',
                  animation: 'blink 1s infinite'
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
});

const ChatModal: React.FC<ChatModalProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isSSEConnected, setIsSSEConnected] = useState(false);
  const [useStreaming, setUseStreaming] = useState(true); // Enable streaming by default

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const initializationAttempted = useRef<boolean>(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);



  // Immediate updates for streaming content provide better user experience

  // SSE callback handlers
  const handleSSEMessage = useCallback((message: ChatApiMessage) => {
    console.log('ChatModal: Received complete SSE message:', message);
    
    // This should only handle complete non-streaming messages
    // Streaming chunks are handled by handleSSEChunk
    // Skip processing to avoid duplicates since streaming builds the complete message
  }, []);

  const handleSSEChunk = useCallback((chunk: string, isComplete: boolean) => {
    console.log('ChatModal: Received SSE chunk:', { chunk, isComplete });
    
    setMessages(prev => {
      // Find the most recent message that's currently streaming
      const lastMessage = prev[prev.length - 1];
      
      if (lastMessage && lastMessage.isStreaming && lastMessage.sender === 'assistant') {
        // Update the existing streaming message
        const updatedMessages = prev.map((msg, index) =>
          index === prev.length - 1
            ? { 
                ...msg, 
                content: msg.content + chunk, 
                isStreaming: !isComplete,
                isReasoningStreaming: isComplete ? false : msg.isReasoningStreaming // Stop reasoning timer when response completes
              }
            : msg
        );
        
        if (isComplete) {
          // Streaming complete
        }
        
        return updatedMessages;
      } else {
        // Create new streaming message only if no current streaming message exists
        const streamingMessageId = `streaming_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const filtered = prev.filter(msg => !msg.isLoading);
        const newMessage = {
          id: streamingMessageId,
          content: chunk,
          sender: 'assistant' as const,
          timestamp: new Date(),
          isStreaming: !isComplete
        };
        
        return [...filtered, newMessage];
      }
    });
  }, []);

  const handleSSEError = useCallback((error: string) => {
    console.error('ChatModal: SSE error:', error);
    setConnectionError(error);
    
    // Add error message if there's a streaming message in progress
    setMessages(prev => prev.map(msg => {
      if (msg.isStreaming) {
        return {
          ...msg,
          isStreaming: false,
          error: error
        };
      }
      return msg;
    }));
    

  }, []);

  const handleSSEConnection = useCallback((connected: boolean) => {
    console.log('ChatModal: SSE connection status:', connected);
    setIsSSEConnected(connected);

    if (!connected) {
      setConnectionError('SSE connection lost');
    } else {
      setConnectionError(null);
    }
  }, []);

  const handleSSEReasoning = useCallback((reasoning: string, isStreaming: boolean) => {
    console.log('ChatModal: Received reasoning chunk:', { reasoning, isStreaming });
    const isComplete = !isStreaming;
    
    if (isComplete) {
      console.log('ChatModal: Reasoning completed, stopping timer');
    }
    
    setMessages(prev => {
      // Find the most recent message that's currently reasoning streaming
      const lastMessage = prev[prev.length - 1];
      
      if (isComplete) {
        console.log('ChatModal: Last message before completion update:', {
          id: lastMessage?.id,
          isReasoningStreaming: lastMessage?.isReasoningStreaming,
          reasoning: lastMessage?.reasoning?.substring(0, 50) + '...'
        });
      }
      
      if (lastMessage && (lastMessage.isStreaming || lastMessage.isReasoningStreaming) && lastMessage.sender === 'assistant') {
        // Update the existing message with reasoning
        const updatedMessages = prev.map((msg, index) =>
          index === prev.length - 1
            ? {
                ...msg,
                // Replace reasoning text instead of concatenating to show current step
                reasoning: reasoning,
                isReasoningStreaming: !isComplete,
                // Set start time on first reasoning chunk
                reasoningStartTime: msg.reasoningStartTime || Date.now(),
                // Calculate duration when complete (in seconds)
                reasoningDuration: isComplete && msg.reasoningStartTime
                  ? Math.floor((Date.now() - msg.reasoningStartTime) / 1000)
                  : msg.reasoningDuration
              }
            : msg
        );
        
        if (isComplete) {
          const updatedLastMessage = updatedMessages[updatedMessages.length - 1];
          console.log('ChatModal: Last message after completion update:', {
            id: updatedLastMessage?.id,
            isReasoningStreaming: updatedLastMessage?.isReasoningStreaming,
            reasoningDuration: updatedLastMessage?.reasoningDuration
          });
        }
        
        return updatedMessages;
      } else {
        // Create new streaming message with reasoning if no current reasoning streaming message
        const streamingMessageId = `streaming_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered, {
          id: streamingMessageId,
          content: '',
          sender: 'assistant' as const,
          timestamp: new Date(),
          isStreaming: true,
          reasoning: reasoning,
          isReasoningStreaming: !isComplete,
          reasoningStartTime: Date.now(),
          reasoningDuration: undefined
        }];
      }
    });
  }, []);

  // Setup SSE callbacks
  useEffect(() => {
    if (!useStreaming) return;

    const unsubscribeMessage = chatAPI.onMessage(handleSSEMessage);
    const unsubscribeChunk = chatAPI.onChunk(handleSSEChunk);
    const unsubscribeError = chatAPI.onError(handleSSEError);
    const unsubscribeConnection = chatAPI.onConnection(handleSSEConnection);
    const unsubscribeReasoning = chatAPI.onReasoning(handleSSEReasoning);

    return () => {
      unsubscribeMessage();
      unsubscribeChunk();
      unsubscribeError();
      unsubscribeConnection();
      unsubscribeReasoning();
    };
  }, [useStreaming, handleSSEMessage, handleSSEChunk, handleSSEError, handleSSEConnection, handleSSEReasoning]);

  const initializeChatConnection = useCallback(async () => {
    // Race condition guard: prevent multiple simultaneous connection attempts
    if (isConnecting) {
      console.log('ChatModal: Connection already in progress');
      return;
    }

    // If session already exists and is connected, no need to reconnect
    if (sessionId && isSSEConnected) {
      console.log('ChatModal: Session already established and connected');
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      console.log('ChatModal: Initializing chat connection...');
      console.log('ChatModal: Initial state - useStreaming:', useStreaming, 'sessionId:', sessionId);

      // Check if chat API is healthy
      const isHealthy = await chatAPI.healthCheck();
      console.log('ChatModal: Health check result:', isHealthy);

      if (!isHealthy) {
        throw new Error('Chat service is not available');
      }

      // Ensure authentication
      if (!chatAPI.isAuthenticated()) {
        console.log('ChatModal: Authenticating...');
        await chatAPI.authenticateTemporary();
        console.log('ChatModal: Authentication completed');
      } else {
        console.log('ChatModal: Already authenticated');
      }

      // Setup chat session (polling-based, no SSE connection needed)
      console.log('ChatModal: Setting up chat session with polling...');
      const newSessionId = await chatAPI.setupStreamingChat(sessionId || undefined);
      console.log('ChatModal: Session created:', newSessionId);
      setSessionId(newSessionId);
      setIsSSEConnected(true); // Set to true to indicate session is ready

      setIsConnecting(false);
      console.log('ChatModal: Connection initialized successfully');
    } catch (error: any) {
      console.error('Failed to initialize chat connection:', error);
      setConnectionError(error.message);
      setIsConnecting(false);
    }
  }, [isConnecting, isSSEConnected, useStreaming, sessionId]);

  // Initialize chat connection when modal opens
  useEffect(() => {
    if (isOpen && !initializationAttempted.current) {
      initializationAttempted.current = true;
      initializeChatConnection();
    } else if (!isOpen) {
      // Reset initialization flag and disconnect SSE when modal closes
      initializationAttempted.current = false;
      if (chatAPI.isChatStreamConnected()) {
        chatAPI.disconnectAllStreams();
      }
      // Reset connection state to allow reconnection when reopened
      // Note: We keep sessionId and messages to preserve conversation history
      setIsSSEConnected(false);
      setConnectionError(null);
    }
  }, [isOpen, initializeChatConnection]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chatAPI.isChatStreamConnected()) {
        chatAPI.disconnectAllStreams();
      }
    };
  }, []);

  const handleSendMessage = async (messageText?: string) => {
    const text = messageText || inputMessage;
    if (!text.trim()) return;

    // Clear any previous connection errors
    setConnectionError(null);

    const newMessage: Message = {
      id: Date.now().toString(),
      content: text,
      sender: 'user',
      timestamp: new Date()
    };

    console.log('ChatModal: Adding user message to state:', newMessage);
    setMessages(prev => {
      const newMessages = [...prev, newMessage];
      console.log('ChatModal: Updated messages array:', newMessages);
      return newMessages;
    });
    setInputMessage('');
    setShowSuggestions(false);
    console.log('ChatModal: showSuggestions set to false');

    try {
      console.log('ChatModal: Attempting to send message:', text);
      console.log('ChatModal: Current state - sessionId:', sessionId);

      if (!sessionId) {
        console.error('No session ID available');
        setMessages(prev => {
          const errorMessage: Message = {
            id: (Date.now() + 2).toString(),
            content: 'Sorry, I\'m having trouble connecting to the chat service. Please try again.',
            sender: 'assistant',
            timestamp: new Date(),
            error: 'No session ID available'
          };
          return [...prev, errorMessage];
        });
        return;
      }

      // Use polling-based streaming
      console.log('ChatModal: Using polling-based streaming');

      // Create placeholder message BEFORE sending for immediate "Thinking" indicator
      const placeholderMessage: Message = {
        id: `streaming_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        content: '',
        sender: 'assistant',
        timestamp: new Date(),
        isStreaming: true,
        isReasoningStreaming: true,
        reasoningStartTime: Date.now(),
        reasoning: ''
      };
      setMessages(prev => [...prev, placeholderMessage]);
      console.log('ChatModal: Created placeholder message for thinking indicator');

      // Send message and start polling
      await chatAPI.sendStreamingMessage(text);

      // Streaming responses will be handled by polling callbacks
      console.log('ChatModal: Polling started, waiting for response...');

    } catch (error: any) {
      console.error('Failed to send message:', error);
      
      // Remove any loading/streaming messages and add error message
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading && !msg.isStreaming);
        const errorMessage: Message = {
          id: (Date.now() + 2).toString(),
          content: 'Sorry, I\'m having trouble connecting to the chat service. Please try again.',
          sender: 'assistant',
          timestamp: new Date(),
          error: error.message
        };
        return [...filtered, errorMessage];
      });
      
      // Reset streaming state if there was an error
      setConnectionError(error.message);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    handleSendMessage(question);
  };

  const resetConversation = () => {
    setMessages([]);
    setShowSuggestions(true);
    setShowDropdown(false);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="w-full max-w-2xl h-[90vh] flex flex-col mx-4"
        style={{
          backgroundColor: '#F7F8F8',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25), 0 8px 24px rgba(0,0,0,0.15)',
          border: '1px solid rgba(255,255,255,0.2)',
          fontFamily: "'Amazon Ember', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between"
          style={{
            padding: '8px 24px',
            backgroundColor: '#F7F8F8',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px'
          }}
        >
          <div className="flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded-full flex items-center justify-center"
              style={{ 
                backgroundColor: isConnecting ? '#888888' : connectionError ? '#CC0C39' : '#FF6900'
              }}
            >
              <Bot className="h-3 w-3 text-white" />
            </div>
            <div>
              <h3 
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: '#0F1111',
                  margin: 0
                }}
              >
                <span style={{ color: '#FF6900', fontWeight: '700' }}>talrev.</span> Assistant
              </h3>
              {(isConnecting || connectionError || useStreaming) && (
                <p style={{
                  fontSize: '12px',
                  color: isConnecting ? '#888888' : connectionError ? '#CC0C39' : isSSEConnected ? '#067D62' : '#FF6900',
                  margin: 0,
                  marginTop: '2px'
                }}>
                  {isConnecting ? 'Initializing...' :
                   connectionError ? 'Connection error' :
                   isSSEConnected ? 'Ready' : 'Initializing...'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200"
                style={{
                  backgroundColor: '#F0F2F2',
                  color: '#0F1111'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#E3E6E6';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#F0F2F2';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <MoreVertical className="h-5 w-5" />
              </button>
              {showDropdown && (
                <div 
                  className="absolute right-0 top-12 z-10"
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    border: '1px solid #D5D9D9',
                    padding: '8px 0',
                    minWidth: '240px'
                  }}
                >
                  <button
                    onClick={resetConversation}
                    className="w-full flex items-center gap-3 transition-colors duration-150"
                    style={{
                      padding: '12px 20px',
                      fontSize: '15px',
                      color: '#0F1111',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F7F8F8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <RotateCcw className="h-5 w-5" style={{ color: '#565959' }} />
                    Reset conversation
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200"
              style={{
                backgroundColor: '#F0F2F2',
                color: '#0F1111',
                border: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#E3E6E6';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#F0F2F2';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Conversation Area */}
        <ScrollArea className="flex-1">
          <div
            style={{
              backgroundColor: '#FFFFFF',
              padding: '24px',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
          {(() => {
            console.log('ChatModal: Render check - messages.length:', messages.length, 'showSuggestions:', showSuggestions);
            console.log('ChatModal: Current messages:', messages);
            return messages.length === 0 && showSuggestions;
          })() && (
            <>
              {/* Welcome Header */}
              <div style={{ marginBottom: '32px' }}>
                <h1 
                  style={{
                    fontSize: 'var(--font-size-3xl)',
                    fontWeight: 'var(--font-weight-bold)',
                    fontFamily: 'var(--font-family-heading)',
                    lineHeight: '1.2',
                    color: '#0F1111',
                    marginBottom: '8px'
                  }}
                >
                  Start a{' '}
                  <span style={{ color: '#FF6900', fontWeight: '800', letterSpacing: '-0.05em' }}>
                    New Conversation
                  </span>
                </h1>
                <p 
                  style={{
                    fontSize: '18px',
                    fontWeight: '400',
                    lineHeight: '1.5',
                    color: '#565959',
                    marginTop: '12px'
                  }}
                >
                  Let's help you find what you need.
                </p>
              </div>
              
              {/* Spacer to push content to bottom */}
              <div style={{ flex: 1 }} />
              
              {/* Suggested Questions and Banner - Flushed to Bottom */}
              <div>
                <div style={{ marginBottom: '24px' }}>
                  <h2 
                    style={{
                      fontSize: '24px',
                      fontWeight: '600',
                      color: '#0F1111',
                      marginBottom: '16px'
                    }}
                  >
                  </h2>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {suggestedQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestedQuestion(question)}
                        className="text-left transition-all duration-200"
                        style={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #D5D9D9',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          fontSize: '15px',
                          color: '#0F1111'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#F7F8F8';
                          e.currentTarget.style.borderColor = '#888888';
                          e.currentTarget.style.transform = 'translateX(4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#FFFFFF';
                          e.currentTarget.style.borderColor = '#D5D9D9';
                          e.currentTarget.style.transform = 'translateX(0)';
                        }}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Update Banner */}
                <div 
                  style={{
                    backgroundColor: '#F0F2F2',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    margin: '16px 0'
                  }}
                >
                  <span style={{ fontSize: '14px', color: '#565959' }}>
                    AI responses may contain inaccuracies. 
                  </span>
                  <button 
                    type="button"
                    style={{
                      color: '#0F1111',
                      fontWeight: '600',
                      textDecoration: 'underline',
                      fontSize: '14px',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#C7511F';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#0F1111';
                    }}
                    onClick={() => {
                      // TODO: Implement issue reporting functionality
                      console.log('Report issue clicked');
                    }}
                  >
                    Report an issue
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Messages */}
          {messages.map((message) => (
            <div key={message.id} style={{ marginBottom: '24px' }}>
              <MessageItem message={message} />
            </div>
          ))}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Section */}
        <div
          style={{
            position: 'sticky',
            bottom: '0',
            backgroundColor: '#FFFFFF',
            padding: '20px 24px',
            borderTop: '1px solid #E3E6E6',
            borderBottomLeftRadius: '24px',
            borderBottomRightRadius: '24px'
          }}
        >

          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              placeholder={connectionError ? "Connection error - click retry" : isConnecting ? "Connecting..." : "Type your question..."}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isConnecting || !!connectionError}
              className="w-full transition-all duration-200"
              style={{
                backgroundColor: (isConnecting || connectionError) ? '#F7F8F8' : '#FFFFFF',
                border: `2px solid ${connectionError ? '#CC0C39' : '#888888'}`,
                borderRadius: '24px',
                padding: '14px 60px 14px 24px',
                fontSize: '16px',
                color: (isConnecting || connectionError) ? '#888888' : '#0F1111',
                outline: 'none',
                cursor: (isConnecting || connectionError) ? 'not-allowed' : 'text'
              }}
              onFocus={(e) => {
                if (!isConnecting && !connectionError) {
                  e.currentTarget.style.borderColor = '#FF6900';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,105,0,0.1)';
                }
              }}
              onBlur={(e) => {
                if (!connectionError) {
                  e.currentTarget.style.borderColor = '#888888';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            />
            <button
              onClick={() => connectionError ? initializeChatConnection() : handleSendMessage()}
              disabled={(!inputMessage.trim() && !connectionError) || isConnecting}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 transition-all duration-200"
              style={{
                backgroundColor: ((!inputMessage.trim() && !connectionError) || isConnecting) ? '#D5D9D9' : connectionError ? '#CC0C39' : '#232F3E',
                color: '#FFFFFF',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                border: 'none',
                cursor: ((!inputMessage.trim() && !connectionError) || isConnecting) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                if (!((!inputMessage.trim() && !connectionError) || isConnecting)) {
                  e.currentTarget.style.backgroundColor = connectionError ? '#A00A2E' : '#37475A';
                  e.currentTarget.style.transform = 'scale(1.05) translateY(-50%)';
                }
              }}
              onMouseLeave={(e) => {
                if (!((!inputMessage.trim() && !connectionError) || isConnecting)) {
                  e.currentTarget.style.backgroundColor = connectionError ? '#CC0C39' : '#232F3E';
                  e.currentTarget.style.transform = 'scale(1) translateY(-50%)';
                }
              }}
              title={connectionError ? 'Retry connection' : 'Send message'}
            >
              {isConnecting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : connectionError ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;